import { httpsCallable } from 'firebase/functions';
import type {
  BillingCancelSubscriptionRequest,
  BillingCancelSubscriptionResponse,
} from '@kawai/shared';
import { functions } from '../firebase.js';

const billingCancelSubscriptionCallable = httpsCallable<
  BillingCancelSubscriptionRequest,
  BillingCancelSubscriptionResponse
>(functions, 'billingCancelSubscription');

/**
 * TP-23: `billingCancelSubscription` Callable の薄いラッパ。
 * PAY.JP `subscriptions.cancel`（解約予約 / current_period_end まで利用可）を呼び、
 * billing_subscriptions ミラーを canceled にする。tenants.billingStatus は期末の
 * subscription.deleted Webhook で 'canceled' に確定する。失敗時は FunctionsError を throw。
 */
export async function callBillingCancelSubscription(
  input: BillingCancelSubscriptionRequest,
): Promise<BillingCancelSubscriptionResponse> {
  const res = await billingCancelSubscriptionCallable(input);
  return res.data;
}

/**
 * Firebase Functions の Callable エラー（FunctionsError）相当かを duck typing で判定する
 * （`code` が `functions/<reason>` 形を満たすか / billingStartSubscription.ts と同方針）。
 */
interface FunctionsErrorLike {
  code: string;
  message?: string;
}

function isFunctionsErrorLike(err: unknown): err is FunctionsErrorLike {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    typeof (err as { code: unknown }).code === 'string' &&
    (err as { code: string }).code.startsWith('functions/')
  );
}

/** `billingCancelSubscription` のエラーを saas-admin 向け日本語メッセージに変換。 */
export function billingCancelSubscriptionErrorMessage(err: unknown): string {
  if (isFunctionsErrorLike(err)) {
    switch (err.code) {
      case 'functions/already-exists':
        return 'このテナントは既に解約済み（解約予約中）です。';
      case 'functions/failed-precondition':
        return `解約できません：${err.message ?? '解約対象の月額サブスクがありません'}`;
      case 'functions/aborted':
        return (
          err.message ??
          'PAY.JP の解約処理に失敗しました。時間をおいて再度お試しください。'
        );
      case 'functions/not-found':
        return '対象のテナントが見つかりません。';
      case 'functions/invalid-argument':
        return `入力値に誤りがあります：${err.message ?? ''}`;
      case 'functions/permission-denied':
        return '権限がありません（saas-admin 権限が必要です）。';
      case 'functions/unauthenticated':
        return 'セッションが切れました。再ログインしてください。';
      default:
        return `解約に失敗しました（${err.code}）。`;
    }
  }
  if (err instanceof Error) {
    return `解約に失敗しました：${err.message}`;
  }
  return '解約に失敗しました。';
}
